import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/primitives';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render-time crash in whatever page is currently mounted, so one
 * broken widget takes down that page rather than the whole app. There is
 * exactly one of these, wrapping the routed content inside Shell — the
 * sidebar and header live outside it, so navigating away is still possible
 * even when the page itself crashed.
 *
 * Before this existed, an uncaught error anywhere in a page (e.g. a library
 * throwing synchronously from a cleanup function) unmounted the entire React
 * tree and left a blank screen with no way back short of reloading the tab.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled error in page:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="py-16">
          <h1 className="section-title mb-2">Something went wrong on this page</h1>
          <p className="max-w-prose text-body text-ink-secondary">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <div className="mt-4 flex gap-3">
            <Button variant="primary" onClick={() => this.setState({ error: null })}>Try again</Button>
            <Button onClick={() => { this.setState({ error: null }); window.location.href = '/'; }}>
              Back to dashboard
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
